import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useEffect, useRef } from 'react';
import { useRoomLiveKit } from '#~/livekit/hooks.js';
import { setSelectedDevice } from '#~/livekit/selection-tracker.js';
import type { LiveKit } from '#~/livekit/types.js';

export type SelectedVideoSyncState = {
  liveKit: LiveKit;
  selectedId: T_Maybe<Device['id']>;
} | null;

/** Synchronize room priority without repeating user-triggered focus side effects. */
export const syncSelectedVideoDevice = (
  liveKit: T_Maybe<LiveKit>,
  selectedId: T_Maybe<Device['id']>,
  previous: SelectedVideoSyncState
): SelectedVideoSyncState => {
  if (!liveKit) return null;
  if (previous?.liveKit === liveKit && previous.selectedId === selectedId) return previous;

  liveKit.setSelectedVideoDevice(selectedId ?? null);
  return { liveKit, selectedId };
};

/** Keep the current selection synchronized when a room broker resource appears or is replaced. */
export const useSelectedVideoSync = (
  roomId: Room['id'],
  selectedId: T_Maybe<Device['id']>,
  enabled: boolean
): void => {
  const previous = useRef<SelectedVideoSyncState>(null);
  const liveKit = useRoomLiveKit(enabled ? roomId : undefined);

  useEffect(() => {
    if (!enabled) {
      previous.current = null;
      return;
    }

    // Record in the room registry BEFORE the instance push — even with no
    // broker resource yet, a LiveKitImpl constructed later seeds its governor
    // from this registry (the recreation gap that paused the selected tile).
    setSelectedDevice(roomId, selectedId ?? null);
    previous.current = syncSelectedVideoDevice(liveKit, selectedId, previous.current);
  }, [enabled, roomId, liveKit, selectedId]);
};
